import React, { useEffect, useMemo, useState } from "react";
import "../fonts/breuer-headline.css";
import "./thankyou.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";

const THANK_YOU_BG = `${process.env.PUBLIC_URL}/assets/game/stage4-bg.jpg`;
const THANK_YOU_BG_MOBILE = `${process.env.PUBLIC_URL}/assets/game/stage4_mob.png`;

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

const FEEDBACK_OPTIONS = [
  { value: "Amazingggg", emoji: "🤩", label: "Amazingggg" },
  { value: "Loved it", emoji: "😍", label: "Loved it" },
  { value: "It was fun", emoji: "😊", label: "It was fun" },
  { value: "It was okay", emoji: "👍", label: "It was okay" },
];

const CONFETTI_MS = 2000;
const CONFETTI_COUNT = 64;

function buildEmojiConfettiPieces() {
  const ts = Date.now();
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    key: `${ts}-${i}`,
    x: Math.random() * 92 + 4,
    y: Math.random() * 85 + 5,
    dx: (Math.random() - 0.5) * 520,
    dy: (Math.random() - 0.5) * 600 + Math.random() * 120,
    rot: Math.random() * 1080 - 540,
    delay: Math.random() * 0.45,
    size: 36 + Math.floor(Math.random() * 44),
  }));
}

function readStoredUser(user) {
  try {
    const fromSession = JSON.parse(sessionStorage.getItem("userData") || "{}");
    if (
      fromSession &&
      typeof fromSession === "object" &&
      (fromSession.userId || fromSession.userid || fromSession.id)
    ) {
      return fromSession;
    }
  } catch {
    /* ignore */
  }
  return user && typeof user === "object" ? user : {};
}

function readThankYouUserId(stored) {
  const id = stored?.userId ?? stored?.userid ?? stored?.id;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

export default function ThankYou() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  const storedUser = useMemo(() => readStoredUser(user), [user]);
  const viewerId = useMemo(() => readThankYouUserId(storedUser), [storedUser]);
  const sessionId = storedUser?.sessionId;
  const organizationId = storedUser?.organizationId;

  const [savedFeedback, setSavedFeedback] = useState("");
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [confettiOn, setConfettiOn] = useState(false);
  const [confettiEmoji, setConfettiEmoji] = useState("");
  const [confettiPieces, setConfettiPieces] = useState(null);

  useEffect(() => {
    if (user?.backButtonRedirect != null) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [user, dispatch]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    const applyBackground = () => {
      const isMobilePortrait = window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
      // old/thankyou.php: dark overlay over the stage4 background.
      body.style.backgroundImage = `linear-gradient(rgba(0, 0, 0, .4), rgba(0, 0, 0, .4)), url("${
        isMobilePortrait ? THANK_YOU_BG_MOBILE : THANK_YOU_BG
      }")`;
      body.style.backgroundSize = isMobilePortrait ? "cover" : "100% 100%";
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundPosition = "center center";
      body.style.backgroundAttachment = "fixed";
    };

    body.classList.remove("common-bg");
    body.classList.add("thank-you-scroll");
    html.style.overflow = "auto";
    body.style.overflow = "auto";
    applyBackground();
    window.addEventListener("resize", applyBackground);
    window.addEventListener("orientationchange", applyBackground);
    return () => {
      body.classList.remove("thank-you-scroll");
      window.removeEventListener("resize", applyBackground);
      window.removeEventListener("orientationchange", applyBackground);
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  // If feedback was already saved in a previous visit, there's nothing left to do here.
  useEffect(() => {
    if (!viewerId || !adminToken || !backendBase || !sessionId || !organizationId) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: viewerId, sessionId, organizationId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data.success || !data.report) return;
        const r = data.report;
        if (r.feedback != null && String(r.feedback).trim() !== "") {
          navigate("/leaderboard", { replace: true });
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewerId, adminToken, backendBase, sessionId, organizationId, navigate]);

  const submitFeedback = async (value) => {
    if (!viewerId || !adminToken || !backendBase || feedbackSaving || savedFeedback) {
      return;
    }
    setFeedbackError("");
    setFeedbackSaving(true);
    try {
      const res = await fetch(`${backendBase}/welcomeStageFeedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: viewerId,
          sessionId,
          organizationId,
          feedback: value,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setFeedbackError(data.message || "Could not save feedback. Please try again.");
        return;
      }
      setSavedFeedback(value);

      const opt = FEEDBACK_OPTIONS.find((o) => o.value === value);
      const emojiChar = opt?.emoji || "";
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (emojiChar && !reduceMotion) {
        setConfettiEmoji(emojiChar);
        setConfettiPieces(buildEmojiConfettiPieces());
        setConfettiOn(true);
        window.setTimeout(() => {
          setConfettiOn(false);
          setConfettiPieces(null);
          setConfettiEmoji("");
          navigate("/leaderboard");
        }, CONFETTI_MS);
      } else {
        navigate("/leaderboard");
      }
    } catch (e) {
      setFeedbackError(e.message || "Could not save feedback.");
    } finally {
      setFeedbackSaving(false);
    }
  };

  const feedbackLocked = Boolean(savedFeedback) || feedbackSaving;
  const canSubmitFeedback = Boolean(viewerId && adminToken && backendBase);

  return (
    <>
      {confettiOn && confettiEmoji && confettiPieces ? (
        <div className="thank-you-confetti" aria-hidden="true">
          {confettiPieces.map((p) => (
            <span
              key={p.key}
              className="thank-you-confetti-emoji"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                fontSize: p.size,
                "--ty-dx": `${p.dx}px`,
                "--ty-dy": `${p.dy}px`,
                "--ty-rot": `${p.rot}deg`,
                animationDelay: `${p.delay}s`,
              }}
            >
              {confettiEmoji}
            </span>
          ))}
        </div>
      ) : null}

      <div className="thank-you-page">
        <section
          className="thank-you-feedback"
          aria-label="How was your experience? Tap an emoji to share your thoughts"
        >
          <h2 className="thank-you-feedback__title">
            How was your  experience? Tap an emoji to share your thoughts 😊
          </h2>
          <div className="thank-you-feedback__grid">
            {FEEDBACK_OPTIONS.map((opt) => {
              const selected = savedFeedback === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`thank-you-feedback__card${
                    selected ? " thank-you-feedback__card--selected" : ""
                  }`}
                  onClick={() => submitFeedback(opt.value)}
                  disabled={!canSubmitFeedback || feedbackLocked}
                  aria-pressed={selected}
                >
                  <span className="thank-you-feedback__emoji" aria-hidden="true">
                    {opt.emoji}
                  </span>
                  <span className="thank-you-feedback__label">{opt.label}</span>
                </button>
              );
            })}
          </div>
          {feedbackError ? (
            <p className="thank-you-feedback__error">{feedbackError}</p>
          ) : null}
          <p className="thank-you-feedback__hint">Your feedback helps us improve!</p>
        </section>
      </div>
    </>
  );
}
